import React, { useState, useLayoutEffect } from 'react';
import styled from 'styled-components/macro';
import ModalDOM from 'styled-react-modal';
import { useMediaQuery } from 'react-responsive';

import Item from './Item';
import ItemDetail from './ItemDetail';
import { IconButton } from '../SubComponents/Buttons';
import { Text } from '../SubComponents/Text';
import { ReactComponent as Prev } from '../Assets/Icons/prev.svg';
import { ReactComponent as Next } from '../Assets/Icons/next.svg';
import useLazyLoad from '../Hooks/useLazyLoad';
import useLocalize from '../Hooks/useLocalize';

const ItemsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const ItemsNav = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;

  @media screen and (max-width: 767px) {
    width: 100%;
  }
`;

export type ItemType = {
  id: string;
  name: string;
  shortDescription?: string | null;
  description?: string | null;
  stock?: number | null;
  price: number;
  imagepath?: string | null;
  rentalItem?: boolean | null;
  rentalStock?: number | null;
};

interface ItemsProps {
  items: ItemType[];
  currentCategory: string;
  itemsPerPage?: number | null;
  onViewItemDetail?: (item: ItemType) => void;
  onChangeItemQuantity?: (item: ItemType, quantity: number) => void;
  onChangeItemsPage?: (index: number) => void;
}

const Items = ({ items, currentCategory, itemsPerPage = 6, ...props }: ItemsProps) => {
  const localize = useLocalize();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const [index, setIndex] = useState(0);
  const [itemOpen, setItemOpen] = useState<ItemType | null>(null);

  // Go to page 1 when the category changes
  useLayoutEffect(() => setIndex(0), [currentCategory]);

  // Lazy load item images
  useLazyLoad([index, itemOpen, items]);

  const pageSize = itemsPerPage ?? 6;

  return (
    <>
      <ItemsNav>
        <IconButton
          altText="Previous page"
          icon={Prev}
          onClick={() => {
            setIndex(index - pageSize);
            if (props.onChangeItemsPage) {
              props.onChangeItemsPage((index - pageSize) / pageSize + 1);
            }
          }}
          disabled={index < pageSize}
        />

        <Text margin="auto">
          {localize('product.page')} {index / pageSize + 1}/
          {Math.ceil(items.length / pageSize)}
        </Text>

        <IconButton
          altText="Next page"
          icon={Next}
          onClick={() => {
            setIndex(index + pageSize);
            if (props.onChangeItemsPage) {
              props.onChangeItemsPage((index + pageSize) / pageSize + 1);
            }
          }}
          disabled={index + pageSize >= items.length}
        />
      </ItemsNav>

      <ItemsWrapper>
        {items.slice(index, index + pageSize).map((item) => (
          <Item
            key={item.id}
            item={item}
            onClick={() => {
              setItemOpen(item);
              if (props.onViewItemDetail) {
                props.onViewItemDetail(item);
              }
            }}
            onChangeItemQuantity={props.onChangeItemQuantity}
            lazyload
          />
        ))}
      </ItemsWrapper>

      <ModalDOM
        isOpen={Boolean(itemOpen)}
        onBackgroundClick={() => setItemOpen(null)}
        onEscapeKeydown={() => setItemOpen(null)}
      >
        {itemOpen && (
          <ItemDetail
            item={itemOpen}
            setItemOpen={setItemOpen}
            lazyload
            onChangeItemQuantity={props.onChangeItemQuantity}
          />
        )}
      </ModalDOM>

      {isMobile && (
        <ItemsNav style={{ marginTop: '32px' }}>
          <IconButton
            altText="Previous page"
            icon={Prev}
            onClick={() => {
              setIndex(index - pageSize);
              if (props.onChangeItemsPage) {
                props.onChangeItemsPage((index - pageSize) / pageSize + 1);
              }
            }}
            disabled={index < pageSize}
          />

          <Text>
            {localize('product.page')} {index / pageSize + 1}/
            {Math.ceil(items.length / pageSize)}
          </Text>

          <IconButton
            altText="Next page"
            icon={Next}
            onClick={() => {
              setIndex(index + pageSize);
              if (props.onChangeItemsPage) {
                props.onChangeItemsPage((index + pageSize) / pageSize + 1);
              }
            }}
            disabled={index + pageSize >= items.length}
          />
        </ItemsNav>
      )}
    </>
  );
};

export default Items;
